import ExcelJS from 'exceljs';
import format from 'date-fns/format';
import { ShoppingListProduct } from 'src/types/shopping_lists/ShoppingList';
import { isValidDate } from 'src/utils/formatTime';
import {
  SHOPPING_LIST_EXCEL_STYLES,
  buildShoppingListExcelWorksheet,
  downloadExcelWorkbook,
} from 'src/utils/shoppingListExcelWorksheetBuilder';
import { formatPriceValueByUom } from 'src/utils/price';

export const SHOPPING_LIST_EXPORT_SHEET_NAME = 'Shopping List';

const EXPORT_DATE_FORMAT = 'M/d/yyyy';

export interface ShoppingListExportSection {
  listName: string;
  products: ShoppingListProduct[];
}

export interface ShoppingListExportValues {
  type: string;
  itemNumber: string;
  brandName: string;
  productName: string;
  catalog: string;
  supplierName: string;
  unit: string;
  casePack: string;
  price: string;
  par: string;
  onHand: string;
  order: string;
  sort: string;
}

interface ShoppingListExportColumn {
  title: string;
  resolve: (values: ShoppingListExportValues) => string;
  minWidth?: number;
}

export const SHOPPING_LIST_EXPORT_COLUMNS: ShoppingListExportColumn[] = [
  { title: 'Type', resolve: (values) => values.type, minWidth: 6 },
  { title: 'Item Number', resolve: (values) => values.itemNumber, minWidth: 11 },
  { title: 'Brand Name', resolve: (values) => values.brandName, minWidth: 10 },
  { title: 'Product Name', resolve: (values) => values.productName },
  { title: 'Catalog', resolve: (values) => values.catalog },
  { title: 'Supplier Name', resolve: (values) => values.supplierName },
  { title: 'Unit', resolve: (values) => values.unit, minWidth: 6 },
  { title: 'Case PK', resolve: (values) => values.casePack, minWidth: 10 },
  { title: 'Price', resolve: (values) => values.price, minWidth: 8 },
  { title: 'Par', resolve: (values) => values.par, minWidth: 6 },
  { title: 'On Hand', resolve: (values) => values.onHand, minWidth: 8 },
  { title: 'Order', resolve: (values) => values.order, minWidth: 10 },
  { title: 'Sort', resolve: (values) => values.sort, minWidth: 5 },
];

const COLUMN_COUNT = SHOPPING_LIST_EXPORT_COLUMNS.length;

/**
 * Fixed widths tuned for US Letter landscape print (all columns on one page width).
 * Type narrow; Product Name widest; Catalog/Supplier medium; remaining columns compact.
 */
export const SHOPPING_LIST_EXPORT_COLUMN_WIDTHS = [
  5, // Type
  9, // Item Number
  9, // Brand Name
  20, // Product Name
  11, // Catalog
  11, // Supplier Name
  5, // Unit
  7, // Case PK
  7, // Price
  4, // Par
  7, // On Hand
  6, // Order
  4, // Sort
] as const;

interface StyledExportRow {
  values: string[];
  kind: 'title' | 'date' | 'header' | 'data' | 'total' | 'spacer';
}

const toExportValue = (value: string | number | null | undefined): string => {
  if (value == null || value === '' || value === '-') {
    return '';
  }

  return String(value);
};

export const formatOrderDate = (value: string | null | undefined): string => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return '';
  }

  if (!isValidDate(trimmed)) {
    return trimmed;
  }

  return format(new Date(trimmed), EXPORT_DATE_FORMAT);
};

export const sortProductsForExport = (products: ShoppingListProduct[]): ShoppingListProduct[] =>
  [...products].sort((left, right) => {
    const leftSort = left.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const rightSort = right.sortOrder ?? Number.MAX_SAFE_INTEGER;
    return leftSort - rightSort;
  });

export const formatShoppingListProductForExport = (
  product: ShoppingListProduct
): ShoppingListExportValues => ({
  type: '',
  itemNumber: toExportValue(product.itemNum),
  brandName: toExportValue(product.brandName),
  productName: toExportValue(product.description),
  catalog: toExportValue(product.catalogName),
  supplierName: toExportValue(product.companyName),
  unit: toExportValue(product.uom),
  casePack: toExportValue(product.casePack),
  price: formatPriceValueByUom(product.price, product.uom),
  par: toExportValue(product.par),
  onHand: '',
  order: formatOrderDate(product.lastPurchaseDate),
  sort: toExportValue(product.sortOrder),
});

const mapProductRows = (products: ShoppingListProduct[]): string[][] =>
  sortProductsForExport(products).map((product) => {
    const values = formatShoppingListProductForExport(product);
    return SHOPPING_LIST_EXPORT_COLUMNS.map((column) => column.resolve(values));
  });

export const getShoppingListExportFileName = (listName: string, date: Date = new Date()): string =>
  `${listName.trim()}_${format(date, 'MM_dd_yyyy')}`;

export const buildShoppingListExportColumnWidths = (): { wch: number }[] =>
  SHOPPING_LIST_EXPORT_COLUMN_WIDTHS.map((wch) => ({ wch }));

const styleForKind = (kind: StyledExportRow['kind']) => {
  switch (kind) {
    case 'title':
      return SHOPPING_LIST_EXCEL_STYLES.title;
    case 'date':
      return SHOPPING_LIST_EXCEL_STYLES.date;
    case 'header':
      return SHOPPING_LIST_EXCEL_STYLES.header;
    case 'total':
      return SHOPPING_LIST_EXCEL_STYLES.total;
    case 'data':
      return SHOPPING_LIST_EXCEL_STYLES.data;
    default:
      return null;
  }
};

const isFullWidthMergedRow = (kind: StyledExportRow['kind']): boolean =>
  kind === 'title' || kind === 'date' || kind === 'total';

export type ShoppingListExportTranslateFn = (
  key: string,
  options?: Record<string, unknown>
) => string;

export const defaultShoppingListExportTranslate: ShoppingListExportTranslateFn = (
  key,
  options
) => {
  if (!options) {
    return key;
  }

  return Object.entries(options).reduce(
    (result, [name, value]) => result.replace(new RegExp(`{{\\s*${name}\\s*}}`, 'g'), String(value)),
    key
  );
};

const buildStyledExportRows = (
  sections: ShoppingListExportSection[],
  exportDate: Date,
  t: ShoppingListExportTranslateFn
): StyledExportRow[] => {
  const rows: StyledExportRow[] = [];
  const titleLabel = t(sections.length === 1 ? 'Shopping List' : 'Shopping Lists');
  const displayDate = format(exportDate, EXPORT_DATE_FORMAT);

  sections.forEach((section, sectionIndex) => {
    if (sectionIndex > 0) {
      rows.push({ values: [], kind: 'spacer' });
    }

    rows.push({
      values: [`${titleLabel}: ${section.listName}`],
      kind: 'title',
    });
    rows.push({
      values: [t('Date: {{date}}', { date: displayDate })],
      kind: 'date',
    });
    rows.push({
      values: SHOPPING_LIST_EXPORT_COLUMNS.map((column) => t(column.title)),
      kind: 'header',
    });

    mapProductRows(section.products).forEach((productRow) => {
      rows.push({ values: productRow, kind: 'data' });
    });

    rows.push({
      values: [t('Total Products: {{count}}', { count: section.products.length })],
      kind: 'total',
    });
  });

  return rows;
};

export const exportShoppingListsToExcel = async (
  sections: ShoppingListExportSection[],
  t: ShoppingListExportTranslateFn = defaultShoppingListExportTranslate
): Promise<void> => {
  if (sections.length === 0) {
    return;
  }

  const exportDate = new Date();
  const styledRows = buildStyledExportRows(sections, exportDate, t);
  const sheetName = t(SHOPPING_LIST_EXPORT_SHEET_NAME);
  const fileName = getShoppingListExportFileName(sections[0].listName, exportDate);
  const workbook = new ExcelJS.Workbook();

  buildShoppingListExcelWorksheet(workbook, sheetName, styledRows, {
    columnCount: COLUMN_COUNT,
    columnWidths: SHOPPING_LIST_EXPORT_COLUMN_WIDTHS,
    isFullWidthMergedRow,
    styleForKind,
  });

  await downloadExcelWorkbook(workbook, fileName);
};
